import type { PaginatedResponse } from '@yawi/domain';

/** Tope de seguridad: si el backend nunca cierra `hasNextPage`, no se cicla. */
const MAX_PAGES = 50;

/**
 * Recorre todas las páginas de un endpoint paginado y devuelve la lista completa.
 *
 * Úsalo en pantallas que muestran el conjunto entero (bibliotecas, historiales
 * agrupados, selectores) en lugar de `getAll*()`: ese método sólo devuelve la
 * primera página, así que cualquier filtro aplicado encimaava sobre datos
 * truncados y el `meta.total` describía otro conjunto.
 *
 * Los filtros se envían al servidor en cada petición, no se aplican en memoria.
 */
export async function fetchAllPages<T>(
  fetchPage: (params: { page: number; limit: number }) => Promise<PaginatedResponse<T>>,
  options?: { pageSize?: number; maxPages?: number; extraParams?: Record<string, unknown> }
): Promise<T[]> {
  const limit = options?.pageSize ?? 100;
  const maxPages = options?.maxPages ?? MAX_PAGES;
  const extra = options?.extraParams ?? {};

  const all: T[] = [];
  let page = 1;
  let hasNextPage = true;

  while (hasNextPage && page <= maxPages) {
    const response = await fetchPage({ ...extra, page, limit });
    all.push(...(response.data ?? []));
    hasNextPage = Boolean(response.meta?.hasNextPage);
    page += 1;
  }

  return all;
}
